import React, { useState } from 'react';
import classnames from 'classnames';
import { IconChevronDownFill } from '@saucelabs/ds-icons';
import { Typography } from '@saucelabs/ds-typography';
import { HStack } from '@saucelabs/sl-component-grid';

import { CREDITS_PER_BLOCK } from '../constants';
import { formatThousand } from '../utils/numbers';

interface FAQWrapperProps {
  collapsible: boolean,
  expanded?: boolean;
  children: React.ReactNode;
}
interface FAQQuestionAnswerProps {
  children: React.ReactNode;
}

const FAQ: {
  Answer: React.FC<FAQQuestionAnswerProps>;
  Question: React.FC<FAQQuestionAnswerProps>;
  Wrapper: React.FC<FAQWrapperProps>;
} = {};

FAQ.Wrapper = ({ children, collapsible, expanded = false }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isExpanded, setExpanded] = useState(!collapsible || expanded);

  return (
    <div className="border-0 border-solid mb-2">
      <div
        className={classnames('group', { 'cursor-pointer': collapsible })}
        onClick={() => setExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
      >
        <HStack
          alignItems="baseline"
          justifyContent="space-between"
        >
          {children[0]}
          {collapsible && (
            <IconChevronDownFill
              className={classnames('fill-current text-gray-600 group-hover:text-blue-700 h-2 w-2 transform transition-transform rotate-0', {
                'rotate-180': isExpanded,
              })}
              role="presentation"
            />
          )}
        </HStack>
      </div>
      {isExpanded && children[1]}
    </div>
  );
};
FAQ.Wrapper.displayName = 'FAQ_Wrapper';

FAQ.Question = ({ children }) => (
  <Typography
    className="text-high-emphasis"
    variant="heading-4"
  >
    {children}
  </Typography>
);
FAQ.Question.displayName = 'FAQ_Question';

FAQ.Answer = ({ children }) => (
  <Typography
    className="text-high-emphasis m-0"
    component="p"
    variant="body-large"
  >
    {children}
  </Typography>
);
FAQ.Answer.displayName = 'FAQ_Answer';

export interface Props {
  collapsible?: boolean,
}

export const HowCreditsWorkFAQ: React.FC<Props> = ({ collapsible = false }) => (
  <section
    className="flex flex-col divide-y divide-gray-300 max-w-76 px-2 mx-auto pb-0 mt-5 mb-5"
    id="how-credits-work"
  >
    <Typography
      className="text-high-emphasis text-center mb-2 mt-0"
      disableSpacing
      variant="heading-3"
    >
      How credits work
    </Typography>
    <FAQ.Wrapper
      collapsible={collapsible}
      expanded
    >
      <FAQ.Question>
        What are credits?
      </FAQ.Question>
      <FAQ.Answer>
        Credits enable you to only pay for the features that you use in Sauce Labs. To use
        them, you simply run your tests. The credits required to run the tests will be
        automatically deducted from your account balance with each test run per device.
        There are no extra fees for running tests or using devices in parallel.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        Credit availability
      </FAQ.Question>
      <FAQ.Answer>
        Credits will be available in your Sauce Labs account immediately after payment has
        been received. You can then run tests, which will automatically consume the credits
        you purchased.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        How many tests can I run?
      </FAQ.Question>
      <FAQ.Answer>
        It depends on the duration of your tests.
        For example, if you run an automated test on
        an Android real device that takes 10 minutes,
        you will use 750 credits.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        Credit expiration
      </FAQ.Question>
      <FAQ.Answer id="credits-expiration">
        Credits expire after 32 days for monthly subscriptions,
        and after 367 days for annual subscriptions.
        Credits with an earlier expiration date are used first.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        Credit refill
      </FAQ.Question>
      <FAQ.Answer>
        When you have fewer than 1,000 credits remaining within a billing cycle,
        {` ${formatThousand(CREDITS_PER_BLOCK, 'standard')} credits will automatically be added to your account. `}
        You’ll be charged $50 plus taxes and fees. Auto-refill credits expire after 32 days.
        You can change your auto-refill preferences in
        {' '}
        <em>My Subscriptions</em>
        {' '}
        at any time.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        Upgrade, downgrade and cancellation
      </FAQ.Question>
      <FAQ.Answer>
        You may upgrade at any time with immediate effect,
        but if you need to downgrade or cancel,
        the update will take effect at the end of your billing cycle.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        Devices, browsers, and operating systems
      </FAQ.Question>
      <FAQ.Answer>
        Test on the most extensive range of Android and iOS devices.
        We support thousands of combinations of browsers and operating systems,
        including more than 200 of the latest emulators and simulators,
        and more than 2,000 iOS and Android devices on our public clouds.
        Public devices are cleaned at the end of each test session.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <FAQ.Wrapper collapsible={collapsible}>
      <FAQ.Question>
        Can I buy concurrency-based Real and Virtual Device Cloud plans?
      </FAQ.Question>
      <FAQ.Answer>
        The previous, concurrency-based plans were replaced by the Credit-Based plan.
        With credits you get more parallel tests (25),
        and affordable entry for automated testing ($50/month),
        and only one plan to access manual and
        automated testing on real and virtual devices.
      </FAQ.Answer>
    </FAQ.Wrapper>
    <div className="border-0 border-solid" />
  </section>
);
export default HowCreditsWorkFAQ;
